<div class="privacy-container">
  <div class="privacy-card">
    <div class="privacy-header">
      <div class="company-name">3Cube Medicare Pvt. Ltd.</div>
      <h1 class="policy-title">Privacy Policy</h1>
      <h2 class="app-subtitle">3CC – Health & Medical Care Management Application</h2>
      <div class="header-divider"></div>
      
      <div class="meta-dates">
        <span class="meta-date"><strong>Effective Date:</strong> 11 June 2026</span>
        <span class="meta-date"><strong>Last Updated:</strong> 11 June 2026</span>
      </div>
    </div>

    <p class="intro-text">
      This Privacy Policy applies to the 3CC mobile application (“App”) published on the Google Play Store by 3Cube Medicare Pvt. Ltd. (“3Cube Medicare”, “we”, “us”, “our”).
    </p>

    <section>
      <h3>1. Introduction</h3>
      <p>
        3CC is a health and medical care management application that enables registered users (“you”, “candidates”) to schedule and manage teleconsultations with our doctors, communicate with our medical team regarding their treatment, and participate in remote psychometric assessments conducted over video call with psychologists at our centre.
      </p>
      <p>
        We are committed to protecting your privacy and handling your personal and health information responsibly, in accordance with applicable laws, including the Digital Personal Data Protection Act, 2023 (India) and the Information Technology Act, 2000 and rules made thereunder. By registering for and using the App, you consent to the practices described in this Privacy Policy.
      </p>
    </section>

    <section>
      <h3>2. Information We Collect</h3>
      
      <h4>a. Personal Information</h4>
      <ul>
        <li>Name, date of birth, gender, and contact details (mobile number, email address);</li>
        <li>Identification or registration details required to verify your candidature with our client organisation;</li>
        <li>Account credentials created during registration.</li>
      </ul>

      <h4>b. Health and Medical Information</h4>
      <ul>
        <li>Medical history, symptoms, treatment details, prescriptions, and consultation notes shared during teleconsultations or queries with our medical team;</li>
        <li>Reports, documents, or images you upload in connection with your treatment;</li>
        <li>Psychometric assessment responses, observations, and evaluation outcomes recorded by our psychologists.</li>
      </ul>

      <h4>c. Audio and Video Data</h4>
      <ul>
        <li>Live audio and video transmitted during teleconsultations and psychometric assessment sessions; </li>
        <li>Recordings of psychometric assessment video sessions, which are made for clinical evaluation, quality assurance, and record-keeping purposes. You will be informed before any session is recorded. </li>
      </ul>

      <h4>d. Technical Information</h4>
      <ul>
        <li>Device type, operating system version, App version, log data, and crash reports used to operate, secure, and improve the App. </li>
      </ul>
      <p>We collect only the information necessary to deliver the App’s medical and assessment services. We do not sell your personal data, and we do not use it for third-party advertising. </p>
    </section>

    <section>
      <h3>3. How We Use Your Information</h3>
      <ul>
        <li>To register your account and verify your eligibility as a candidate of our client.</li>
        <li>To schedule, conduct, and manage teleconsultations with our doctors.</li>
        <li>To respond to your queries regarding your treatment through our medical team.</li>
        <li>To conduct, record, and evaluate remote psychometric assessments with our psychologists.</li>
        <li>To maintain your medical and assessment records as required by professional and legal obligations. </li>
        <li>To communicate appointment reminders, reports, and service-related notifications.</li>
        <li>To maintain the security, performance, and reliability of the App. </li>
      </ul>
    </section>

    <section>
      <h3>4. App Permissions</h3>
      <p>The App requests the following device permissions, used solely for the purposes stated: </p>
      <ul>
        <li><strong>Camera</strong> – for video teleconsultations and psychometric assessment sessions.</li>
        <li><strong>Microphone</strong> – for audio during consultations and assessments.</li>
        <li><strong>Notifications</strong> – or appointment and service reminders</li>
        <li><strong>Storage / Files (where applicable)</strong> – to upload medical reports or documents you choose to share.</li>
      </ul>
    </section>

    <section>
      <h3>5. Sharing and Disclosure of Information</h3>
      <p>We share your information only on a need-to-know basis, with appropriate confidentiality safeguards.</p>
      <ul>
        <li>Our medical professionals – doctors, psychologists, and medical support staff engaged in your care or assessment.</li>
        <li>Our medical professionals – doctors, psychologists, and medical support staff engaged in your care or assessment.</li>
        <li>Service providers – trusted technology providers (e.g., secure cloud hosting and video platforms) who process data on our behalf under confidentiality obligations.</li>
        <li>Legal requirements – where disclosure is required by law, regulation, court order, or lawful request of a government authority. </li>
      </ul>
      <p>
        We do not sell, rent, or trade your personal or health information to any third party. 
      </p>
    </section>

    <section>
      <h3>6. Data Security</h3>
      <p>
        We implement reasonable technical and organisational security measures to protect your information, including encrypted transmission, access controls limiting data access to authorised personnel only, and secure storage of medical records and session recordings. While no system can be guaranteed to be absolutely secure, we continuously review and strengthen our safeguards. 
      </p>
    </section>

    <section>
      <h3>7. Data Retention</h3>
      <p>
        We retain personal and health information, including assessment session recordings, only for as long as necessary to fulfil the purposes described in this Policy and to comply with applicable medical record-keeping and legal requirements. When information is no longer required, it is securely deleted or anonymised. 
      </p>
    </section>

    <section>
      <h3>8. Your Rights</h3>
      <p>Subject to applicable law, you have the right to: </p>
      <ul>
        <li>Access the personal information we hold about you</li>
        <li>Request correction of inaccurate or incomplete information</li>
        <li>Request deletion of your personal data, subject to legal and medical record retention obligations</li>
        <li>Withdraw consent for processing, which may affect our ability to provide the services</li>
        <li>Raise a grievance regarding the handling of your personal data</li>
      </ul>
    </section>

    <section>
      <h3>9. Account and Data Deletion</h3>
      
      <p>
        You may request deletion of your account and associated personal data at any time by writing to <strong>3cchelp&#64;3cubemedicare.com</strong> from your registered email address, with the subject line “Account Deletion Request”. We will process verified requests within a reasonable period, except where retention of certain records is required by law or professional medical regulations. 
        <!-- To request deletion, email: <strong>3cchelp&#64;3cubemedicare.com</strong> from your registered email address with the subject "Account Deletion Request". -->
      </p>
    </section>

    <section>
      <h3>10. Children's Privacy</h3>
      <p>
        The App is intended for use by adults registered as candidates of our client organisations. We do not knowingly collect personal information from children. If you believe a child has provided us personal data, please contact us so we may take appropriate action.
      </p>
    </section>

    <section>
      <h3>11. Contact Us / Grievance Redressal</h3>
      <p>For any questions, concerns, requests, or grievances relating to this Privacy Policy or your personal data, please contact: </p>
      <p><strong>3Cube Medicare Pvt. Ltd.</strong></p>
      <p>Email: 3cchelp&#64;3cubemedicare.com</p>
    </section>

    <section>
      <h3>12. Changes to This Privacy Policy</h3>
      
      <p>
       We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The updated version will be posted on our public Privacy Policy page with a revised “Last Updated” date. Continued use of the App after changes are posted constitutes your acceptance of the updated Policy.
      </p>
    </section>

    <div class="privacy-footer">
      © 2026 3Cube Medicare Pvt. Ltd. All rights reserved.
    </div>
  </div>
</div>
